import { FC, PropsWithChildren, createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { challengeMfa } from "../api/login";
import { StorageKeys } from "../constants/constants";
import { useNavigateClient } from "../hooks";
import { enableMultifactorAuthentication } from "../utils/featureFlags";
import { AuthenticationContext } from "./AuthenticationContext";
import { ExperienceContext } from "./ExperienceContext";
import { MemberEligibilityContext } from "./MemberEligibilityContext";

export const MultifactorAuthenticationContext = createContext({
  enforceMfaSetup: false,
  isOnMfaSetupPage: false,
  setEnforceMfaSetup: (_: boolean) => {},
  isLoadingMultifactorAuth: true,
});

export const MultifactorAuthenticationContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { getAuthToken, setAuthState, authState } = useContext(AuthenticationContext);
  const { isLoadingEligibility } = useContext(MemberEligibilityContext);
  const { isLoadingExperience } = useContext(ExperienceContext);
  const location = useLocation();
  const navigate = useNavigateClient();

  const [hasCheckedMfaRequired, setHasCheckedMfaRequired] = useState(false);
  const [enforceMfaSetup, setEnforceMfaSetup] = useState(false);
  const [isLoadingMultifactorAuth, setIsLoadingMultifactorAuth] = useState(false);
  const isEmulating = !!sessionStorage.getItem("isEmulation");

  // when the member signs out or hits the landing page, clear the state here
  useEffect(() => {
    if (authState === "signedOut") {
      setHasCheckedMfaRequired(false);
      setEnforceMfaSetup(false);
      setIsLoadingMultifactorAuth(false);
    }
  }, [authState]);

  const runMfaCheck = async () => {
    const loggedIn = authState === "signedIn";
    const authenticator = localStorage.getItem(StorageKeys.AUTH_SOURCE);

    if (authenticator === "aws/cognito" && !isEmulating) {
      if (loggedIn && !hasCheckedMfaRequired && !isLoadingMultifactorAuth) {
        setIsLoadingMultifactorAuth(true);
        try {
          const mfaResponse = await challengeMfa(await getAuthToken());
          setEnforceMfaSetup(mfaResponse.force_mfa);
          setHasCheckedMfaRequired(true);
          if (mfaResponse.force_mfa) {
            navigate("/mfa-setup");
          }
        } catch {
          setHasCheckedMfaRequired(true);
          setEnforceMfaSetup(false);
          setAuthState("signedIn");
        } finally {
          setIsLoadingMultifactorAuth(false);
        }
      } else if (loggedIn && enforceMfaSetup && location.pathname !== "/mfa-setup") {
        navigate("/mfa-setup");
      } else if (!loggedIn) {
        setHasCheckedMfaRequired(false);
        setEnforceMfaSetup(false);
      }
    } else if (authenticator === "capitalrx/sts") {
      // bypass any checks for SSO users
      setHasCheckedMfaRequired(true);
      setEnforceMfaSetup(false);
    }
  };

  // check the mfa setup for the member
  useEffect(() => {
    if (enableMultifactorAuthentication() && !isEmulating) {
      if(!isLoadingEligibility && !isLoadingExperience) {
        runMfaCheck();
      }
    } else {
      setIsLoadingMultifactorAuth(false);
      setHasCheckedMfaRequired(true);
    }
  }, [location, authState, isLoadingEligibility, isLoadingExperience]);

  return (
    <MultifactorAuthenticationContext.Provider
      value={{
        enforceMfaSetup: enforceMfaSetup,
        isOnMfaSetupPage: location.pathname === "/mfa-setup",
        setEnforceMfaSetup: setEnforceMfaSetup,
        isLoadingMultifactorAuth: isLoadingMultifactorAuth || !hasCheckedMfaRequired,
      }}
    >
      {children}
    </MultifactorAuthenticationContext.Provider>
  );
};
